import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	CONTENT_WIDTH,
	FONT_SIZE,
	GRID_RADIUS,
	HOLIDAY_GROUPS,
	INK,
	LETTER_SPACING,
	LINE_HEIGHT,
	NEUTRAL,
	SHADOW,
	STATUS_BADGE,
	SURFACE,
} from "../../../theme/tokens";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { ClickableStatTile } from "../../UIToolkit/molecules/ClickableStatTile";
import { type HolidayGroup, type HolidayPerson, MAX_HOLIDAY_GROUPS, MIN_HOLIDAY_GROUPS } from "./holidayApi";
import { countPeopleByGroup, holidayGroupColor, holidayGroupLabel } from "./holidayFormat";
import { useSetHolidayGroupCount } from "./holidayQueries";

type Props = {
	groups: HolidayGroup[];
	/** Active people; undefined while loading. */
	people: HolidayPerson[] | undefined;
	canEdit: boolean;
	/** Jumps to the Not assigned list. */
	onShowNotAssigned: () => void;
};

const GROUP_COUNTS = Array.from({ length: MAX_HOLIDAY_GROUPS - MIN_HOLIDAY_GROUPS + 1 }, (_, index) => MIN_HOLIDAY_GROUPS + index);

/** The top of the Holidays tab: every group as a tile with its head count, and how many groups there are. */
export const HolidayGroupsHero: FC<Props> = ({ groups, people, canEdit, onShowNotAssigned }) => {
	const { t } = useTranslation();
	const setCount = useSetHolidayGroupCount();
	const titleId = useId();
	const counts = useMemo(() => (people === undefined ? undefined : countPeopleByGroup(people)), [people]);
	const countItems = useMemo<DropdownItem<number>[]>(() => GROUP_COUNTS.map((count) => ({ value: count, label: String(count) })), []);

	return (
		<Box
			component="section"
			aria-labelledby={titleId}
			data-testid="holiday-groups-hero"
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 2.5,
				p: { xs: 2, md: 3 },
				borderRadius: GRID_RADIUS.PANEL,
				border: `1px solid ${alpha(theme.palette.primary.main, HOLIDAY_GROUPS.HERO_BORDER_ALPHA)}`,
				background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, HOLIDAY_GROUPS.HERO_WASH_START_ALPHA)} 0%, ${alpha(theme.palette.primary.main, HOLIDAY_GROUPS.HERO_WASH_MID_ALPHA)} 45%, ${SURFACE} 100%)`,
				boxShadow: SHADOW.PANEL_SOFT,
			})}
		>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 2 }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxWidth: CONTENT_WIDTH.ANNOTATION }}>
					<Typography
						id={titleId}
						component="h2"
						sx={{ fontSize: FONT_SIZE.TITLE_XXL, fontWeight: 700, letterSpacing: LETTER_SPACING.HEADLINE, color: STATUS_BADGE.BRAND_FALLBACK }}
					>
						{t("holidays.groups.title")}
					</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY, lineHeight: LINE_HEIGHT.SNUG }}>{t("holidays.groups.description")}</Typography>
				</Box>
				<SystemDropdown<DropdownItem<number>>
					items={countItems}
					selectedItem={groups.length}
					onChange={(count) => {
						if (count !== undefined && count !== groups.length) setCount.mutate(count);
					}}
					label={t("holidays.groups.count")}
					disabled={!canEdit || setCount.isPending}
					search={false}
					formControlSx={{ width: HOLIDAY_GROUPS.GROUP_COUNT_SELECT_WIDTH_PX, backgroundColor: SURFACE, borderRadius: 1 }}
					data-testid="holiday-group-count"
				/>
			</Box>
			<Box
				sx={{
					display: "grid",
					// auto-fit: two groups get wide tiles, five still share one row on a laptop.
					gridTemplateColumns: `repeat(auto-fit, minmax(${HOLIDAY_GROUPS.GROUP_TILE_MIN_WIDTH_PX}px, ${HOLIDAY_GROUPS.GROUP_TILE_MAX_WIDTH_PX}px))`,
					gap: 1.5,
				}}
			>
				{groups.map((group, index) => {
					// DEFAULT: a group nobody is in yet has no entry in the counts map.
					const count = counts === undefined ? undefined : (counts.byGroupId.get(group.id) ?? 0);
					const color = holidayGroupColor(groups, group.id);
					return (
						<ClickableStatTile
							key={group.id}
							entranceIndex={index}
							leading={group.name}
							leadingColor={color}
							accentColor={color.dot}
							label={holidayGroupLabel(t, group)}
							// DEFAULT: the figure is a skeleton while loading, so this 0 is never shown.
							value={count ?? 0}
							loading={count === undefined}
							valueSuffix={t("holidays.groups.staffSuffix", { count: count ?? 0 })}
							dataTestId={`holiday-group-tile-${group.name}`}
						/>
					);
				})}
				<ClickableStatTile
					entranceIndex={groups.length}
					leading={<PersonOffOutlinedIcon fontSize="inherit" sx={{ color: NEUTRAL[500] }} />}
					dashed
					label={t("holidays.group.notAssigned")}
					// DEFAULT: as above — a skeleton while loading, so this 0 is never shown.
					value={counts?.notAssigned ?? 0}
					loading={counts === undefined}
					valueSuffix={t("holidays.groups.staffSuffix", { count: counts?.notAssigned ?? 0 })}
					onClick={onShowNotAssigned}
					ariaLabel={t("holidays.groups.showNotAssigned")}
					dataTestId="holiday-group-tile-not-assigned"
				/>
			</Box>
		</Box>
	);
};
